import { Link } from "react-router-dom";
import { ArrowRight, Zap, BookOpen, AlertCircle, Briefcase } from "lucide-react";

const KIND_META = {
  news: { icon: Zap, label: { en: "News", fr: "Actu" }, color: "#00A878", to: (i) => `/news/${i.id}` },
  insight: { icon: BookOpen, label: { en: "Insight", fr: "Analyse" }, color: "#FFD400", to: (i) => `/insights/${i.id}` },
  alert: { icon: AlertCircle, label: { en: "Alert", fr: "Alerte" }, color: "#ef4444", to: (i) => `/alerts/${i.id}` },
  job: { icon: Briefcase, label: { en: "Job", fr: "Job" }, color: "#2563eb", to: (i) => `/jobs/${i.id}` },
};

const Signal = ({ kind, item, lang, idx }) => {
  if (!item) return null;
  const meta = KIND_META[kind] || KIND_META.news;
  const Icon = meta.icon;
  return (
    <Link
      to={meta.to(item)}
      data-testid={`top-signal-${idx}`}
      className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors p-4 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2 text-[10px] font-mono-dl tracking-[0.18em] uppercase">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-white"
          style={{ backgroundColor: meta.color }}
        >
          <Icon size={11} /> {meta.label[lang] || meta.label.en}
        </span>
        <span className="text-[#9ca3af]">{item.published_at}</span>
      </div>
      <h3 className="font-display text-[17px] leading-[1.25] text-[#111827] group-hover:text-[#00A878] line-clamp-3">
        {item.title}
      </h3>
      {item.company && (
        <div className="text-[12px] text-[#4B5563]">{item.company}{item.location ? ` · ${item.location}` : ""}</div>
      )}
    </Link>
  );
};

/**
 * Top signals of the week — compact 3-card editorial band.
 * Surfaces one high-impact news, one insight, and one alert/job depending on
 * what's been most relevant in the last 7 days.
 */
const TopSignals = ({ data, lang }) => {
  if (!data) return null;
  const items = [data.news, data.insight, data.third].filter(Boolean);
  if (items.length === 0) return null;

  return (
    <section data-testid="top-signals-section" className="dl-container pt-8 pb-2">
      <div className="flex items-baseline justify-between border-b border-[#111827] pb-2 mb-5">
        <div className="flex items-baseline gap-3">
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">
            {lang === "fr" ? "01 / Cette semaine" : "01 / This week"}
          </div>
          <h2 className="font-display text-xl sm:text-2xl text-[#111827] leading-tight">
            {lang === "fr" ? "Top signaux" : "Top signals"}
          </h2>
        </div>
        <span className="text-[11px] font-mono-dl uppercase tracking-wider text-[#9ca3af] hidden sm:inline-flex items-center gap-1">
          <ArrowRight size={11} /> 7d
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {items.map((s, i) => (
          <Signal key={`${s.kind}-${i}`} kind={s.kind} item={s.item} lang={lang} idx={i} />
        ))}
      </div>
    </section>
  );
};

export default TopSignals;
